import React, { useState, useEffect } from 'react';
import useToken from '../context/useToken';
import axios from 'axios';
import { BASE_URL } from '../utils';

const ParentComponent = ({ id, title, subtitle, description, img,fetchPageInfo }) => {
  const token = useToken();
  const [data, setData] = useState({
    id: id || '', // Default to an empty string or other default value if id is not available
    img: img || '', // Default to an empty string if img is not passed
    title: title || '', // Default to an empty string if title is not passed
    subtitle: subtitle || '', // Default to an empty string if subtitle is not passed
    description: description || '', // Default to an empty string if description is not passed
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Only update state if the props are different to avoid unnecessary re-renders
    if (id && title && subtitle && description && img) {
      setData({
        id,
        img,
        title,
        subtitle,
        description,
      });
    }
  }, [id, title, subtitle, description, img]); // Dependencies to update the state when these props change

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setData({
        ...data,
        img: file,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('pageName', 'about_page');  // Replace with actual pageName
    formData.append('title', data.title);
    formData.append('subtitle', data.subtitle);
    formData.append('description', data.description);
    formData.append('isActive', 'true');  // or replace with a boolean if needed

    if (data.img) {
      const imageFile = data.img instanceof File ? data.img : null; // Check if img is a File object
      if (imageFile) {
        formData.append('image', imageFile);
      }
    }

    try {
      const response = await axios.put(
        `https://thecfagroup.org/v1/api/webpages/${id}`,
        formData,
        {
          headers: {
            'Authorization':
              `Bearer ${token}`,
            'accept': '/',
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      console.log('Response:', response.data);
      fetchPageInfo()
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating data:', error);
    }
  };

  // Check if all necessary data is available before rendering
  if (!data.id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  

  return (
    <div>
      {/* Background Section */}
      <div
        className="relative h-[50vh] bg-cover bg-center z-1"
        style={{ backgroundImage: `url(${data.img})` }}
      >
        <div className="absolute inset-0 bg-[#03000c] opacity-75"></div>
        <div className="absolute z-1 flex flex-col items-start justify-center min-h-full w-[70%] px-4 mt-6 sm:px-10 md:px-16">
          <div className="text-white flex items-center gap-8 text-left px-4 py-6 w-full max-w-3xl mx-auto">
            <h1 className="text-[40px] sm:text-[50px] md:text-[70px] font-extrabold leading-tight mb-4 animate-fade-in-up">
              {data.title}
            </h1>
            {token && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-12 h-12 cursor-pointer"
                onClick={handleOpenModal}
              >
                <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* About Us Content */}
      <div className="flex justify-center items-center py-16 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="w-full max-w-6xl p-8 bg-white shadow-xl rounded-lg">
          <h1 className="text-3xl md:text-5xl font-bold mb-6 text-left text-[#25a182]">
            {data.subtitle}
          </h1>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
            {data.description}
          </p>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[90%] max-w-full">
            <h2 className="text-2xl font-bold mb-4">Edit Content</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Title</label>
                <input
                  type="text"
                  name="title"
                  value={data.title}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                />
              </div>
              <div>
                <label className="block font-semibold mb-2">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border p-2 rounded"
                />
              </div>
              <div>
                <label className="block font-semibold mb-2">Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={data.subtitle}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                />
              </div>
              <div>
                <label className="block font-semibold mb-2">Description</label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={handleChange}
                  className="w-full border p-2 rounded h-36"
                ></textarea>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="bg-red-500 text-white px-4 py-2 rounded"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#25a182] text-white px-4 py-2 rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentComponent;
